/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type {
  CSSColorStrict,
  HallowElementProps,
  ThemeType,
} from "@packages/ui";
import {
  CheckmarkIcon,
  CloseIcon,
  constantSemanticColors,
  numericPercentages,
  radius,
  ScrollArea,
  shades,
  stroke,
} from "@packages/ui";
import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";

import { HighlightColors } from "./select.utilities";

type HighlightsProps = HallowElementProps<"div"> & {
  highlight: HighlightColors | null;
  setHighlight: Dispatch<SetStateAction<HighlightColors | null>>;
  filter?: Set<HighlightColors> | null;
  variant: "popover" | "edit" | "filter";
};

const highlightContainer: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.colors.transparent,
  paddingInline: theme.spacing.l,
  paddingTop: theme.spacing.m,
  paddingBottom: theme.spacing.xl,
  width: theme.spacing.full,
});

const bibleViewContainer: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  borderTopLeftRadius: radius.ms,
  borderTopRightRadius: radius.ms,
  borderWidth: stroke.regular,
  borderStyle: "solid",
  borderColor: theme.semanticColors.neutralsLowest,
  boxShadow: `0 10px 15px -3px ${shades.shade5}, 0 4px 6px -2px ${shades.shade10}`,
});

const optionsContainer: Interpolation = {
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
  gap: 10,
};

const highlightCircle: Interpolation<HighlightColors> = (highlight) => {
  let color: CSSColorStrict;
  switch (highlight) {
    case HighlightColors.Pink:
      color = "#F8B2FF";
      break;
    case HighlightColors.Aqua:
      color = "#54F7E9";
      break;
    case HighlightColors.Green:
      color = "#C4FE50";
      break;
    case HighlightColors.Blue:
      color = "#99C9FF";
      break;
    case HighlightColors.Purple:
      color = "#C098F4";
      break;
    case HighlightColors.Red:
      color = "#FFA2A2";
      break;
    case HighlightColors.Orange:
      color = "#F6BF56";
      break;
    case HighlightColors.Yellow:
    default:
      color = "#FFDE32";
      break;
  }
  return {
    backgroundColor: color,
    borderColor: `color-mix(in srgb, ${color}, #000000 5%)`,
    borderRadius: radius.full,
    borderStyle: "solid",
    borderWidth: stroke.regular,
    cursor: "pointer",
    flexShrink: 0,
    height: 30,
    position: "relative",
    width: 30,
    ":hover": {
      backgroundColor: `color-mix(in srgb, ${color}, #000000 10%)`,
      borderColor: `color-mix(in srgb, ${color}, #000000 15%)`,
    },
  };
};

const selectedOverlay: Interpolation = {
  alignItems: "center",
  backgroundColor: constantSemanticColors.fixedDarkenMed,
  borderRadius: radius.full,
  display: "flex",
  height: 30,
  justifyContent: "center",
  left: numericPercentages[50],
  position: "absolute",
  top: numericPercentages[50],
  transform: "translate(-50%, -50%)",
  width: 30,
};

export const Highlights = ({
  highlight,
  setHighlight,
  filter = null,
  variant,
  ...props
}: HighlightsProps) => {
  const options = useMemo(() => {
    const arr = Object.values(HighlightColors).filter(
      (v) => typeof v === "number",
    );
    if (filter === null) return arr;
    return arr.filter((color) => filter.has(color));
  }, [filter]);
  return (
    <div
      css={[highlightContainer, variant === "popover" && bibleViewContainer]}
      {...props}
    >
      <ScrollArea
        maskLeft
        maskRight
        scrollbarHorizontalCSS={{ display: "none" }}
      >
        <div css={optionsContainer}>
          {options.map((color) => (
            <button
              type="button"
              key={color}
              css={highlightCircle(color)}
              onClick={() => {
                if (filter !== null && highlight === color) setHighlight(null);
                else setHighlight(color);
              }}
            >
              {highlight === color ? (
                <div css={selectedOverlay}>
                  {variant === "filter" ? (
                    <CloseIcon fill={constantSemanticColors.onColorHighest} />
                  ) : (
                    <CheckmarkIcon.SimpleOn
                      fill={constantSemanticColors.onColorHighest}
                    />
                  )}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
